import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import userService from '../../../../../services/user.service';
import clinicService from '../../../../../services/clinic.service';
import { toast } from 'react-toastify';
const REMINDER_TOGGLES = [
    { name: 'apptReminder', label: 'Appointment Reminders' },
    { name: 'isPrebookedReminder', label: 'Prebooked Reminders' },
    { name: 'isFirstReminder', label: 'First Reminders' },
    { name: 'isSecondReminder', label: 'Second Reminders' },
    { name: 'isLastReminder', label: 'Last Reminders' },
    { name: 'isSameDayReminder', label: 'Same Day Reminders' },
    { name: 'isClReplacement', label: 'Contact Lens Reminders' },
    { name: 'isClReorder', label: 'Contact Lens Reorder' },
    { name: 'isBirthday', label: 'Birthday Reminders' },
    { name: 'isThankyou', label: 'Thank You Reminder' },
    { name: 'isWelcome', label: 'Welcome Reminders' },
    { name: 'isEmail', label: 'Email Reminder' },
    { name: 'isSms', label: 'SMS Reminders' },
    { name: 'isCall', label: 'Call Reminders' },
    { name: 'isApptSummary', label: 'Appointment Summary' },
    { name: 'isGlPickup', label: 'Glasses Pickup' },
    { name: 'isClPickup', label: 'Contact Lens Pickup' },
    { name: 'isFollowup', label: 'Follow Up' },
    { name: 'isClGlSummary', label: 'Contact Lens & Glass Summary' },
];

function ClinicUserReminderSettingsForm({ reminderSettings, user, updateSettings, onCancel }) {
    const { register, handleSubmit, reset } = useForm({
        defaultValues: {
            ...reminderSettings,
            apptSummary: reminderSettings.apptSummary || 'ECP',
            callPreference: reminderSettings.callPreference || 'HOME_PHONE',
        },
    });

    const [isLoading, setIsLoading] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            const formattedData = {
                ...data,
                id: reminderSettings.id,
                clinicId: user.clinicId,
                userId: user.id,
                daysPrebookReminder: parseInt(data.daysPrebookReminder, 10),
                daysFirstReminder: parseInt(data.daysFirstReminder, 10),
                daysSecondReminder: parseInt(data.daysSecondReminder, 10),
                minutesToAppt: parseInt(data.minutesToAppt, 10),
            };

            const response = await userService.updateUserReminderSettings(user.clinicId, user.id, formattedData);

            toast.success('Reminder settings updated', { position: toast.POSITION.BOTTOM_RIGHT });
            updateSettings?.(response.data);
        } catch (err) {
            toast.error(err.message || 'Error updating reminder settings', { position: toast.POSITION.BOTTOM_RIGHT });
        } finally {
            setIsLoading(false);
        }
    };

    const getClinicReminders = async () => {
        try {
            setIsLoading(true);
            const response = await clinicService.getReminderSettings(user.clinicId);
            reset({
                ...response.data,
                id: reminderSettings.id,
                clinicId: user.clinicId,
                userId: user.id,
            });
            setResetSuccess(true);
            setTimeout(() => setResetSuccess(false), 2000);
        } catch (err) {
            toast.error('Failed to load clinic reminder settings', { position: toast.POSITION.BOTTOM_RIGHT });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex justify-content-end mb-3">
                <button type="button" className="btn btn-outline-warning btn-sm" onClick={getClinicReminders} disabled={isLoading}>
                    {resetSuccess ? (
                        <>
                            <i className="bi bi-check me-1"></i>
                            Reset Complete
                        </>
                    ) : (
                        <>
                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                            Reset to Clinic Settings
                        </>
                    )}
                </button>
            </div>

            <div className="row g-3">
                <div className="col-lg-7">
                    <div className="notifications-panel">
                        <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                            <span className="d-flex align-items-center gap-2">
                                <i className="bi bi-bell"></i>
                                Reminder Toggles
                            </span>
                            {onCancel && (
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={onCancel} disabled={isLoading}>
                                    <i className="bi bi-x-lg me-1"></i>
                                    Cancel
                                </button>
                            )}
                        </div>
                        <div className="notifications-panel__body">
                            {REMINDER_TOGGLES.map((item) => (
                                <div key={item.name} className="clinic-users-reminder-toggle-row">
                                    <span>{item.label}</span>
                                    <div className="form-check form-switch mb-0">
                                        <input className="form-check-input" type="checkbox" role="switch" disabled={isLoading} {...register(item.name)} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-lg-5">
                    <div className="notifications-panel">
                        <div className="notifications-panel__header">
                            <i className="bi bi-sliders"></i>
                            Reminder Preferences
                        </div>
                        <div className="notifications-panel__body">
                            <div className="mb-3">
                                <label className="notifications-form-label">Call Preference</label>
                                <select className="form-select form-select-sm" disabled={isLoading} {...register('callPreference')}>
                                    <option value="DISABLED">Disabled</option>
                                    <option value="HOME_PHONE">Home Phone</option>
                                    <option value="CELL_PHONE">Cell Phone</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Appointment Summary</label>
                                <select className="form-select form-select-sm" disabled={isLoading} {...register('apptSummary')}>
                                    <option value="CLINIC">Clinic</option>
                                    <option value="ECP">ECP</option>
                                    <option value="BOTH">Both</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Reminder Time</label>
                                <input type="time" className="form-control form-control-sm" step="1" disabled={isLoading} {...register('reminderTime')} />
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Same Day Reminder Time</label>
                                <input type="time" className="form-control form-control-sm" step="1" disabled={isLoading} {...register('sameDayReminderTime')} />
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Days to Send Prebook Reminder</label>
                                <input type="number" className="form-control form-control-sm" disabled={isLoading} {...register('daysPrebookReminder', { valueAsNumber: true })} />
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Days to Send First Reminder</label>
                                <input type="number" className="form-control form-control-sm" disabled={isLoading} {...register('daysFirstReminder', { valueAsNumber: true })} />
                            </div>
                            <div className="mb-3">
                                <label className="notifications-form-label">Days to Send Second Reminder</label>
                                <input type="number" className="form-control form-control-sm" disabled={isLoading} {...register('daysSecondReminder', { valueAsNumber: true })} />
                            </div>
                            <div className="mb-0">
                                <label className="notifications-form-label">Minutes Earlier</label>
                                <input type="number" className="form-control form-control-sm" disabled={isLoading} {...register('minutesToAppt', { valueAsNumber: true })} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="clinic-users-form-actions">
                {onCancel && (
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={isLoading}>
                        Cancel
                    </button>
                )}
                <button type="submit" className="btn btn-success btn-sm" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Saving…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-floppy me-1"></i>
                            Save Settings
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}

export default ClinicUserReminderSettingsForm;
